import React, {
  useEffect,
  useCallback,
  useState,
  useRef,
  useMemo,
} from "react";

import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Connection,
  ConnectionMode,
  MarkerType,
  NodeTypes,
  EdgeTypes,
  OnNodesChange,
  OnEdgesChange,
  applyNodeChanges,
  applyEdgeChanges,
  NodeChange,
  EdgeChange,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  useAgentWorkflowStore,
  AgentWorkflowDefinition,
  FlowEditorNode,
  FlowEditorEdge,
  Node,
} from "@/store/agentsStore";
import {
  createTriggerNode,
  createActionNode,
  createEdge,
  syncNodePositions,
  workflowNodesToReactFlow,
  workflowEdgesToReactFlow,
} from "./utils/canvasUtils";
import {
  parseExportPayload,
  hasWorkflowContent,
  AgentWorkflowExportPayload,
} from "./utils/importExportUtils";
import { Button } from "@/components/ui/button";
import {
  X,
  Save,
  Undo,
  Redo,
  Activity,
  MessageSquare,
  Edit,
  ExternalLink,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { EditableDefaultEdge } from "./components/EditableDefaultEdge";
import { ActionNode as ActionNodeComponent } from "./components/ActionNode";
import { TriggerNode as TriggerNodeComponent } from "./components/TriggerNode";
import { ConditionNode } from "./components/ConditionNode";
import { AgentNode as AgentNodeComponent } from "./components/AgentNode";
import { SelectActionModal } from "./components/SelectActionModal";
import { SelectTriggerModal } from "./components/SelectTriggerModal";
import {
  ActionDefinition,
  useAgentWorkflowActionsStore,
} from "@/store/agentWorkflowActionsStore";
import {
  TriggerDefinition,
  useAgentWorkflowTriggersStore,
} from "@/store/agentWorkflowTriggersStore";
import { NodeEditPanel } from "./components/NodeEditPanel";
import { EditAgentDialog } from "./components/EditAgentDialog";
import { useAutoSave } from "../quotesDeprecated/QuotePreview/hooks/useAutoSave";
import { useWorkflowHistory } from "./hooks/useWorkflowHistory";
import { useHandleExport } from "./hooks/useHandleExport";
import { DEFAULT_AGENT_TIMEZONE } from "@forgepoint/shared";

interface AgentEditorSheetProps {
  agentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Edge types need to be created inside the component to access handleUpdateEdgeCondition
const createEdgeTypes = (
  onUpdateEdgeCondition: (edgeId: string, condition: string) => void,
): EdgeTypes => ({
  DEFAULT: (props) => (
    <EditableDefaultEdge
      {...props}
      onUpdateEdgeCondition={onUpdateEdgeCondition}
    />
  ),
});

const nodeTypes: NodeTypes = {
  ACTION: ActionNodeComponent,
  TRIGGER: TriggerNodeComponent,
  CONDITION: ConditionNode,
  AGENT: AgentNodeComponent,
  MESSAGE: ActionNodeComponent,
};

const defaultEdgeOptions = {
  type: "DEFAULT" as const,
  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 20,
    height: 20,
  },
};

export function AgentEditorSheet({
  agentId,
  open,
  onOpenChange,
}: AgentEditorSheetProps) {
  const {
    getAgent,
    getAgentById,
    getAgentState,
    getWorkflowVersions,
    getWorkflowDefinition,
    updateWorkflowDefinition: updateWorkflowDefinitionStore,
    updateWorkflowVersionPrompt,
    updateAgent,
    saveWorkflowVersion,
    deployWorkflow,
    getVersionState,
  } = useAgentWorkflowStore();
  const { fetchActionsByIds, getActionById } = useAgentWorkflowActionsStore();
  const { fetchTriggersByIds, getTriggerById } =
    useAgentWorkflowTriggersStore();

  const currentAgent = getAgentById(agentId);
  const agentState = getAgentState(agentId);
  const workflowDefinition = getWorkflowDefinition(agentId);
  const versionState = getVersionState(agentId);
  const latestVersion = useAgentWorkflowStore((state) =>
    state.getLatestWorkflowVersion(agentId),
  );

  const loading = agentState.isLoadingGet || versionState.isLoadingGet;
  const error = agentState.getError || versionState.getError;
  const isSaving = versionState.isLoadingSave;
  const isDeploying = versionState.isLoadingDeploy;

  // React Flow canvas state (separate from workflow definition)
  const [reactFlowNodes, setReactFlowNodes] = useState<FlowEditorNode[]>([]);
  const [reactFlowEdges, setReactFlowEdges] = useState<FlowEditorEdge[]>([]);

  // Modal state
  const [showActionModal, setShowActionModal] = useState(false);
  const [showTriggerModal, setShowTriggerModal] = useState(false);
  const [showEditAgentDialog, setShowEditAgentDialog] = useState(false);

  // Import/export state
  const [isExporting, setIsExporting] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importJson, setImportJson] = useState("");
  const [importError, setImportError] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [pendingImportPayload, setPendingImportPayload] =
    useState<AgentWorkflowExportPayload | null>(null);
  const isImportConfirmStep = Boolean(pendingImportPayload);
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);

  // Edit panel state
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [showEditPanel, setShowEditPanel] = useState(false);

  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const historyInitKeyRef = useRef<string | null>(null);

  const {
    recordSnapshot,
    undo: undoHistory,
    redo: redoHistory,
    canUndo,
    canRedo,
    resetHistory,
  } = useWorkflowHistory();

  const { queueEdit } = useAutoSave<AgentWorkflowDefinition>({
    onSave: async (_data) => {
      await saveWorkflowVersion(agentId);
    },
  });

  const handleExport = useHandleExport({
    agentId,
    setImportError,
    setIsExportDropdownOpen,
    setIsExporting,
  });

  const hasExistingContent = useMemo(
    () => hasWorkflowContent(workflowDefinition, latestVersion?.prompt),
    [workflowDefinition, latestVersion?.prompt],
  );

  const applyImportPayload = useCallback(
    async (payload: AgentWorkflowExportPayload) => {
      setIsImporting(true);
      try {
        updateWorkflowVersionPrompt(agentId, {
          prompt: payload.prompt ?? undefined,
          timezone: payload.timezone,
          loadRules: payload.loadRules ?? false,
        });

        // Apply the new definition to local state and store
        setReactFlowNodes(
          workflowNodesToReactFlow(payload.definition, latestVersion?.id),
        );
        setReactFlowEdges(workflowEdgesToReactFlow(payload.definition));
        updateWorkflowDefinitionStore(agentId, payload.definition);
        queueEdit(payload.definition);

        toast.success("Agent workflow imported successfully");
        setIsImportDialogOpen(false);
        setPendingImportPayload(null);
        setImportJson("");
        setImportError(null);
      } catch (_error) {
        toast.error("Failed to import agent workflow", {
          description:
            "No changes have been applied. Please check the JSON and try again.",
        });
      } finally {
        setIsImporting(false);
      }
    },
    [
      agentId,
      latestVersion,
      queueEdit,
      updateWorkflowDefinitionStore,
      updateWorkflowVersionPrompt,
    ],
  );

  const handleImportValidate = useCallback(async () => {
    setImportError(null);

    const payload = parseExportPayload(importJson);

    if (!payload) {
      setImportError(
        "The provided text is not valid agent export JSON. Please check the format and try again.",
      );
      return;
    }

    if (hasExistingContent) {
      setPendingImportPayload(payload);
      return;
    }

    await applyImportPayload(payload);
  }, [applyImportPayload, hasExistingContent, importJson]);

  const handleImportDialogOpenChange = useCallback((nextOpen: boolean) => {
    setIsImportDialogOpen(nextOpen);
    if (!nextOpen) {
      setImportError(null);
      setImportJson("");
      setPendingImportPayload(null);
    }
  }, []);

  // Load agent and workflow versions when sheet opens
  useEffect(() => {
    if (open && agentId) {
      getAgent(agentId);
      getWorkflowVersions(agentId);
    }
  }, [open, agentId, getAgent, getWorkflowVersions]);

  // Fetch specific actions and triggers used in the workflow
  useEffect(() => {
    if (!workflowDefinition || !workflowDefinition.nodes) {
      return;
    }

    // Collect all unique action IDs from the workflow
    const actionIds = new Set<string>();
    const triggerIds = new Set<string>();

    workflowDefinition.nodes.forEach((node) => {
      if (node.type === "TRIGGER") {
        const trigger = getTriggerById(node.triggerId);
        if (!trigger) {
          triggerIds.add(node.triggerId);
        }
      }

      if (
        node.type === "ACTION" ||
        node.type === "CONDITION" ||
        node.type === "MESSAGE"
      ) {
        const action = getActionById(node.actionId);
        if (!action) {
          actionIds.add(node.actionId);
        }
      }

      if (node.type === "AGENT") {
        const action = getActionById(node.actionId);
        if (!action) {
          actionIds.add(node.actionId);
        }
        node.actions
          ?.map((agentAction) => agentAction.actionId)
          .map((id) => {
            const agentAction = getActionById(id);
            if (!agentAction) {
              actionIds.add(id);
            }
            return id;
          });
      }
    });

    // Fetch the specific actions and triggers
    if (actionIds.size > 0) {
      fetchActionsByIds(Array.from(actionIds));
    }
    if (triggerIds.size > 0) {
      fetchTriggersByIds(Array.from(triggerIds));
    }
  }, [
    workflowDefinition,
    fetchActionsByIds,
    fetchTriggersByIds,
    getActionById,
    getTriggerById,
  ]);

  const persistWorkflowDefinition = useCallback(
    (
      definition: AgentWorkflowDefinition,
      options?: { skipHistory?: boolean },
    ) => {
      updateWorkflowDefinitionStore(agentId, definition);
      queueEdit(definition);
      if (!options?.skipHistory) {
        recordSnapshot(definition);
      }
    },
    [agentId, recordSnapshot, updateWorkflowDefinitionStore, queueEdit],
  );

  const workflowHistoryKey = latestVersion?.id
    ? `${agentId}-${latestVersion.id}`
    : agentId;

  useEffect(() => {
    if (!workflowDefinition) {
      return;
    }

    if (historyInitKeyRef.current === workflowHistoryKey) {
      return;
    }

    setReactFlowNodes(
      workflowNodesToReactFlow(workflowDefinition, latestVersion?.id),
    );
    setReactFlowEdges(workflowEdgesToReactFlow(workflowDefinition));
    resetHistory(workflowDefinition);
    historyInitKeyRef.current = workflowHistoryKey;
  }, [workflowDefinition, workflowHistoryKey, latestVersion?.id, resetHistory]);

  // Handle React Flow node changes
  const handleNodesChange: OnNodesChange<FlowEditorNode> = useCallback(
    (changes: NodeChange<FlowEditorNode>[]) => {
      if (!workflowDefinition) {
        return;
      }

      // Apply React Flow changes to canvas state
      const updatedReactFlowNodes = applyNodeChanges<FlowEditorNode>(
        changes,
        reactFlowNodes,
      );
      setReactFlowNodes(updatedReactFlowNodes);

      // Sync positions back to workflow definition
      const updatedNodes = syncNodePositions(
        workflowDefinition.nodes,
        updatedReactFlowNodes,
      );

      // Remove deleted nodes and associated edges
      const nodeIds = updatedReactFlowNodes.map((n) => n.id);
      const filteredNodes = updatedNodes.filter((n) => nodeIds.includes(n.id));
      const filteredNodeIds = new Set(filteredNodes.map((n) => n.id));
      const filteredEdges = workflowDefinition.edges.filter(
        (e) => filteredNodeIds.has(e.from) && filteredNodeIds.has(e.to),
      );

      const shouldRecordHistory = changes.some(
        (change) =>
          change.type !== "position" ||
          change.dragging === false ||
          change.dragging === undefined,
      );

      persistWorkflowDefinition(
        {
          ...workflowDefinition,
          nodes: filteredNodes,
          edges: filteredEdges,
        },
        { skipHistory: !shouldRecordHistory },
      );
    },
    [workflowDefinition, reactFlowNodes, persistWorkflowDefinition],
  );

  // Handle React Flow edge changes
  const handleEdgesChange: OnEdgesChange<FlowEditorEdge> = useCallback(
    (changes: EdgeChange<FlowEditorEdge>[]) => {
      if (!workflowDefinition) {
        return;
      }

      // Apply React Flow changes to canvas state
      const updatedReactFlowEdges = applyEdgeChanges<FlowEditorEdge>(
        changes,
        reactFlowEdges,
      );
      setReactFlowEdges(updatedReactFlowEdges);

      // Update the workflow definition
      const edgeIds = updatedReactFlowEdges.map((e) => e.id);
      const filteredEdges = workflowDefinition.edges.filter((e) =>
        edgeIds.includes(e.id),
      );

      persistWorkflowDefinition({
        ...workflowDefinition,
        edges: filteredEdges,
      });
    },
    [workflowDefinition, reactFlowEdges, persistWorkflowDefinition],
  );

  // Handle connection between nodes
  const onConnect = useCallback(
    (params: Connection) => {
      if (!workflowDefinition || !params.source || !params.target) {
        return;
      }

      const newEdge = createEdge(params.source, params.target);

      // Update workflow definition
      persistWorkflowDefinition({
        ...workflowDefinition,
        edges: [...workflowDefinition.edges, newEdge],
      });

      // Update canvas state
      const newReactFlowEdge: FlowEditorEdge = {
        id: newEdge.id,
        source: newEdge.from,
        target: newEdge.to,
        data: { edge: newEdge },
        ...defaultEdgeOptions,
      };
      setReactFlowEdges((edges) => [...edges, newReactFlowEdge]);
    },
    [workflowDefinition, persistWorkflowDefinition],
  );

  // Handle trigger selection from modal
  const handleTriggerSelection = useCallback(
    (trigger: TriggerDefinition) => {
      if (!workflowDefinition) {
        return;
      }

      const newNode = createTriggerNode(trigger.id, { x: 250, y: 50 });

      // Update workflow definition
      persistWorkflowDefinition({
        ...workflowDefinition,
        nodes: [...workflowDefinition.nodes, newNode],
      });

      // Update canvas state
      const newReactFlowNode: FlowEditorNode = {
        id: newNode.id,
        type: "TRIGGER",
        position: newNode.position,
        data: {
          node: newNode,
          workflowVersionId: latestVersion?.id,
        },
      };
      setReactFlowNodes((nodes) => [...nodes, newReactFlowNode]);
    },
    [workflowDefinition, persistWorkflowDefinition, latestVersion?.id],
  );

  // Handle action selection from modal
  const handleActionSelection = useCallback(
    (action: ActionDefinition) => {
      if (!workflowDefinition) {
        return;
      }

      const newNode = createActionNode(action.id, action.type, {
        x: 250,
        y: 200,
      });

      // Update workflow definition
      persistWorkflowDefinition({
        ...workflowDefinition,
        nodes: [...workflowDefinition.nodes, newNode],
      });

      // Update canvas state
      const newReactFlowNode: FlowEditorNode =
        newNode.type === "AGENT"
          ? {
              id: newNode.id,
              type: "AGENT",
              position: newNode.position,
              data: {
                node: newNode,
                workflowVersionId: latestVersion?.id,
              },
            }
          : {
              id: newNode.id,
              type: newNode.type,
              position: newNode.position,
              data: {
                node: newNode,
                workflowVersionId: latestVersion?.id,
              },
            };
      setReactFlowNodes((nodes) => [...nodes, newReactFlowNode]);
    },
    [workflowDefinition, persistWorkflowDefinition, latestVersion?.id],
  );

  // Handle node click to open edit panel
  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: FlowEditorNode) => {
      const workflowNode = workflowDefinition?.nodes.find(
        (n) => n.id === node.id,
      );
      if (workflowNode) {
        setSelectedNode(workflowNode);
        setShowEditPanel(true);
      }
    },
    [workflowDefinition],
  );

  const handleUpdateEdgeCondition = useCallback(
    (edgeId: string, condition: string) => {
      if (!workflowDefinition) {
        return;
      }
      const currEdge = workflowDefinition.edges.find((e) => e.id === edgeId);
      if (!currEdge) {
        return;
      }

      const updatedEdge = { ...currEdge, condition };

      const updatedEdges = workflowDefinition.edges.map((edge) =>
        edge.id === edgeId ? updatedEdge : edge,
      );

      setReactFlowEdges((edges) =>
        edges.map((edge) =>
          edge.id === edgeId ? { ...edge, data: { edge: updatedEdge } } : edge,
        ),
      );

      persistWorkflowDefinition({
        ...workflowDefinition,
        edges: updatedEdges,
      });
    },
    [workflowDefinition, persistWorkflowDefinition],
  );

  // Create edge types with the update handler
  const edgeTypes = useMemo(
    () => createEdgeTypes(handleUpdateEdgeCondition),
    [handleUpdateEdgeCondition],
  );

  // Handle node update from edit panel
  const handleNodeUpdate = useCallback(
    (updatedNode: Node) => {
      if (!workflowDefinition) {
        return;
      }

      const updatedNodes = workflowDefinition.nodes.map((n) =>
        n.id === updatedNode.id ? updatedNode : n,
      );

      persistWorkflowDefinition({
        ...workflowDefinition,
        nodes: updatedNodes,
      });

      // Update the selected node state to keep it in sync
      setSelectedNode(updatedNode);
    },
    [workflowDefinition, persistWorkflowDefinition],
  );

  const applyDefinitionSnapshot = useCallback(
    (definition: AgentWorkflowDefinition) => {
      setReactFlowNodes(
        workflowNodesToReactFlow(definition, latestVersion?.id),
      );
      setReactFlowEdges(workflowEdgesToReactFlow(definition));
      persistWorkflowDefinition(definition, { skipHistory: true });
      if (selectedNode) {
        const refreshedNode = definition.nodes.find(
          (node) => node.id === selectedNode.id,
        );
        if (refreshedNode) {
          setSelectedNode(refreshedNode);
        } else {
          setSelectedNode(null);
          setShowEditPanel(false);
        }
      }
    },
    [latestVersion?.id, persistWorkflowDefinition, selectedNode],
  );

  const handleUndo = useCallback(() => {
    const previousDefinition = undoHistory();
    if (!previousDefinition) {
      return;
    }
    applyDefinitionSnapshot(previousDefinition);
  }, [undoHistory, applyDefinitionSnapshot]);

  const handleRedo = useCallback(() => {
    const nextDefinition = redoHistory();
    if (!nextDefinition) {
      return;
    }
    applyDefinitionSnapshot(nextDefinition);
  }, [redoHistory, applyDefinitionSnapshot]);

  // Handle workflow deployment
  const handleDeploy = useCallback(async () => {
    const success = await deployWorkflow(agentId);
    if (success) {
      // Refresh workflow versions to get the updated status
      await getWorkflowVersions(agentId);
    }
  }, [agentId, deployWorkflow, getWorkflowVersions]);

  // Handle agent and workflow version updates
  const handleEditAgentSave = useCallback(
    async (data: {
      name: string;
      description?: string;
      prompt?: string;
      timezone: string;
      loadRules: boolean;
    }) => {
      // Update the agent name and description
      if (
        data.name !== currentAgent?.name ||
        data.description !== currentAgent?.description
      ) {
        await updateAgent(agentId, {
          name: data.name,
          description: data.description,
        });
      }

      const currentTimezone = latestVersion?.timezone ?? DEFAULT_AGENT_TIMEZONE;
      const currentLoadRules = latestVersion?.loadRules ?? false;
      const promptChanged = data.prompt !== latestVersion?.prompt;
      const timezoneChanged = data.timezone !== currentTimezone;
      const loadRulesChanged = data.loadRules !== currentLoadRules;

      if (promptChanged || timezoneChanged || loadRulesChanged) {
        updateWorkflowVersionPrompt(agentId, {
          prompt: data.prompt,
          timezone: data.timezone,
          loadRules: data.loadRules,
        });
        await saveWorkflowVersion(agentId);
      }
    },
    [
      agentId,
      currentAgent,
      latestVersion,
      updateAgent,
      updateWorkflowVersionPrompt,
      saveWorkflowVersion,
    ],
  );

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full max-w-none sm:max-w-none p-0 overflow-auto">
          {loading ? (
            <div className="h-full flex flex-col">
              <div className="border-b px-4 py-3 sm:px-6 sm:py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 sm:gap-4">
                    <Skeleton className="h-8 w-8" />
                    <Skeleton className="h-6 w-32 sm:w-48" />
                  </div>
                  <Skeleton className="h-9 w-20 sm:w-24" />
                </div>
              </div>
              <div className="flex-1 p-4 sm:p-6">
                <div className="space-y-4">
                  <Skeleton className="h-24 sm:h-32 w-full" />
                  <Skeleton className="h-48 sm:h-64 w-full" />
                </div>
              </div>
            </div>
          ) : error || !currentAgent ? (
            <div className="h-full flex items-center justify-center p-4">
              <div className="text-center">
                <p className="text-muted-foreground mb-4 text-sm sm:text-base">
                  {error || "Failed to load agent"}
                </p>
                <Button onClick={() => onOpenChange(false)}>Close</Button>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col">
              <div className="h-full flex flex-col">
                <SheetHeader className="border-b px-3 py-2 sm:px-4 sm:py-3 flex-shrink-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                      <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 sm:h-8 sm:w-8 flex-shrink-0"
                          onClick={() => onOpenChange(false)}
                        >
                          <X className="h-5 w-5 sm:h-4 sm:w-4" />
                        </Button>
                        <SheetTitle className="text-base sm:text-lg font-semibold flex items-center gap-1 sm:gap-2 min-w-0">
                          <span className="truncate max-w-[100px] sm:max-w-none">
                            {currentAgent.name}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 sm:h-6 sm:w-6 flex-shrink-0"
                            onClick={() => setShowEditAgentDialog(true)}
                          >
                            <Edit className="h-3.5 w-3.5 sm:h-3 sm:w-3" />
                          </Button>
                        </SheetTitle>
                      </div>
                      {latestVersion && (
                        <span
                          className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs rounded flex-shrink-0 ${
                            latestVersion.status === "ACTIVE"
                              ? "bg-green-100 text-green-700"
                              : latestVersion.status === "RETIRED"
                                ? "bg-gray-100 text-gray-600"
                                : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {latestVersion.status === "ACTIVE"
                            ? "Active"
                            : latestVersion.status === "RETIRED"
                              ? "Retired"
                              : "Draft"}
                        </span>
                      )}
                      {isSaving && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1 flex-shrink-0">
                          <Save className="h-3 w-3 animate-pulse" />
                          <span className="hidden sm:inline">Saving...</span>
                        </span>
                      )}
                      {versionState.lastSavedAt && !isSaving && (
                        <span className="text-xs text-muted-foreground hidden sm:inline flex-shrink-0">
                          Last saved{" "}
                          {new Date(
                            versionState.lastSavedAt,
                          ).toLocaleTimeString()}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                      <DropdownMenu
                        open={isExportDropdownOpen}
                        onOpenChange={setIsExportDropdownOpen}
                      >
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="relative h-9 w-9 sm:h-8 sm:w-8"
                            disabled={isExporting}
                            aria-label="Open export and import menu"
                          >
                            <ExternalLink className="h-5 w-5 sm:h-4 sm:w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onSelect={(event) => {
                              event.preventDefault();
                              handleExport();
                            }}
                            disabled={isExporting}
                            className="flex items-center gap-2"
                          >
                            <span>Export Agent</span>
                            {isExporting && (
                              <div className="h-3 w-3 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setImportJson("");
                              setImportError(null);
                              setPendingImportPayload(null);
                              handleImportDialogOpenChange(true);
                            }}
                          >
                            Import Agent
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <Button
                        size="sm"
                        className="h-9 sm:h-8"
                        onClick={handleDeploy}
                        disabled={
                          isDeploying ||
                          Object.keys(latestVersion?.errors || {}).length > 0
                        }
                      >
                        Deploy
                        {isDeploying && (
                          <div className="ml-2 h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        )}
                      </Button>
                    </div>
                  </div>
                </SheetHeader>

                <div className="flex-1 relative" ref={reactFlowWrapper}>
                  <ReactFlow
                    nodes={reactFlowNodes}
                    edges={reactFlowEdges}
                    onNodesChange={handleNodesChange}
                    onEdgesChange={handleEdgesChange}
                    onConnect={onConnect}
                    onNodeClick={handleNodeClick}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    defaultEdgeOptions={defaultEdgeOptions}
                    connectionMode={ConnectionMode.Loose}
                    fitView
                    className="bg-gray-50"
                  >
                    <Background />
                    <Controls className="!left-2 !bottom-16 sm:!bottom-4" />
                    <MiniMap
                      nodeColor={(node) => {
                        if (node.type === "trigger") {
                          return "#fbbf24";
                        }
                        return "#6b7280";
                      }}
                      style={{
                        backgroundColor: "#f9fafb",
                        border: "1px solid #e5e7eb",
                      }}
                      className="hidden sm:block"
                    />
                  </ReactFlow>

                  <div className="absolute bottom-4 sm:bottom-6 left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-lg border p-1.5 sm:p-2 flex items-center gap-1 sm:gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex items-center gap-1 sm:gap-2 h-10 sm:h-8 px-2 sm:px-3"
                      onClick={() => setShowTriggerModal(true)}
                    >
                      <MessageSquare className="h-4 w-4" />
                      <span className="hidden sm:inline">Trigger</span>
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex items-center gap-1 sm:gap-2 h-10 sm:h-8 px-2 sm:px-3"
                      onClick={() => setShowActionModal(true)}
                    >
                      <Activity className="h-4 w-4" />
                      <span className="hidden sm:inline">Action</span>
                    </Button>

                    <div className="w-px h-6 bg-gray-200 mx-0.5 sm:mx-1" />

                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 sm:h-8 sm:w-8"
                      onClick={handleUndo}
                      disabled={!canUndo}
                    >
                      <Undo className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 sm:h-8 sm:w-8"
                      onClick={handleRedo}
                      disabled={!canRedo}
                    >
                      <Redo className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <Dialog
        open={isImportDialogOpen}
        onOpenChange={handleImportDialogOpenChange}
      >
        <DialogContent className="w-[calc(100%-2rem)] max-w-lg mx-auto">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">
              {isImportConfirmStep
                ? "Confirm workflow import"
                : "Import agent workflow JSON"}
            </DialogTitle>
            <DialogDescription className="text-sm">
              {isImportConfirmStep
                ? "Importing will replace the current workflow definition, prompt, and timezone for this agent. This action cannot be undone."
                : "Paste a previously exported agent workflow JSON payload. The payload should include definition, prompt, and timezone fields."}
            </DialogDescription>
          </DialogHeader>

          {!isImportConfirmStep && (
            <div className="space-y-3">
              <Textarea
                value={importJson}
                onChange={(event) => setImportJson(event.target.value)}
                rows={8}
                className="text-sm min-h-[150px] sm:min-h-[200px]"
                placeholder="Paste the exported agent workflow JSON here"
              />
              {importError && (
                <p className="text-sm text-red-600">{importError}</p>
              )}
            </div>
          )}

          {isImportConfirmStep && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                The existing workflow graph and prompt for this agent will be
                overwritten. Do you want to continue?
              </p>
            </div>
          )}

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:gap-0">
            <Button
              variant="outline"
              type="button"
              className="w-full sm:w-auto"
              onClick={() => {
                if (isImportConfirmStep) {
                  setPendingImportPayload(null);
                  return;
                }
                handleImportDialogOpenChange(false);
              }}
              disabled={isImporting}
            >
              {isImportConfirmStep ? "No" : "Cancel"}
            </Button>
            {isImportConfirmStep ? (
              <Button
                type="button"
                className="w-full sm:w-auto"
                onClick={() => {
                  if (pendingImportPayload) {
                    applyImportPayload(pendingImportPayload);
                  }
                }}
                disabled={isImporting || !pendingImportPayload}
              >
                Yes, import workflow
                {isImporting && (
                  <div className="ml-2 h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                )}
              </Button>
            ) : (
              <Button
                type="button"
                className="w-full sm:w-auto"
                onClick={() => {
                  handleImportValidate();
                }}
                disabled={isImporting}
              >
                Import workflow
                {isImporting && (
                  <div className="ml-2 h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SelectActionModal
        open={showActionModal}
        onOpenChange={setShowActionModal}
        onSelectAction={handleActionSelection}
      />

      <SelectTriggerModal
        open={showTriggerModal}
        onOpenChange={setShowTriggerModal}
        onSelectTrigger={handleTriggerSelection}
      />

      {selectedNode && (
        <NodeEditPanel
          node={selectedNode}
          isOpen={showEditPanel}
          onClose={() => {
            setShowEditPanel(false);
            setSelectedNode(null);
          }}
          onChangeNode={handleNodeUpdate}
          edges={workflowDefinition?.edges || []}
          onUpdateEdgeCondition={handleUpdateEdgeCondition}
        />
      )}

      {currentAgent && (
        <EditAgentDialog
          agent={currentAgent}
          workflowVersion={latestVersion}
          open={showEditAgentDialog}
          onOpenChange={setShowEditAgentDialog}
          onSave={handleEditAgentSave}
          isSaving={agentState.isLoadingUpdate || versionState.isLoadingSave}
        />
      )}
    </>
  );
}
